import type { CapabilityConfig, CapabilitySpec } from './types';
import type { Action, Event } from './constraints';
import {
  AppsDockControl,
  SidebarMenuControl,
  MultiplayerCall,
  MultiplayerRoom,
  CallFrameApplet,
  ConfigApplet,
  ModalApplet,
  HeadlessApplet,
  PageApplet,
  OverlayApplet,
  PostCallApplet,
  DashboardApplet,
  ReceiveContext,
  PingPong,
  Storage,
  FileTransfer,
  FileService,
  Backend,
  Notification,
  AppToken,
  PhotoCapture,
  PostInstall,
  Transcription,
  AlertBanner,
  AppEvents,
  PlaySound,
  NavigationInterception,
  FeatureFlag,
} from './capabilities';
import { IframedRuntime } from './runtimes';
import { System } from './system';

const Specs: CapabilitySpec[] = [
  PingPong,
  ReceiveContext,
  MultiplayerCall,
  MultiplayerRoom,
  IframedRuntime,
  CallFrameApplet,
  ConfigApplet,
  ModalApplet,
  HeadlessApplet,
  PageApplet,
  OverlayApplet,
  PostCallApplet,
  DashboardApplet,
  AppsDockControl,
  SidebarMenuControl,
  Storage,
  FileTransfer,
  FileService,
  AppToken,
  Backend,
  Notification,
  PhotoCapture,
  PostInstall,
  System,
  Transcription,
  AlertBanner,
  AppEvents,
  PlaySound,
  NavigationInterception,
  FeatureFlag,
];

export const EventTypeMap: Record<
  string,
  { spec: CapabilitySpec; event: Event }
> = {};
export const ActionTypeMap: Record<
  string,
  { spec: CapabilitySpec; action: Action }
> = {};

for (const spec of Specs) {
  if (spec.Events) {
    for (const [event, slug] of Object.entries(spec.Events)) {
      if (slug in EventTypeMap) {
        throw new Error(`💥 Capability event collision: ${slug}`);
      }
      EventTypeMap[slug] = { spec: spec, event: event as Event };
    }
  }
  if (spec.Actions) {
    for (const [action, slug] of Object.entries(spec.Actions)) {
      if (slug in ActionTypeMap) {
        throw new Error(`💥 Capability action collision: ${slug}`);
      }
      ActionTypeMap[slug] = { spec: spec, action: action as Action };
    }
  }
}

export function byId(id: string): CapabilitySpec | undefined {
  return Specs.find((spec) => spec.id === id);
}

// Look up a CapabilitySpec by an event string that may or may not exist
export function byEventType(type: string): CapabilitySpec | undefined {
  return EventTypeMap[type]?.spec;
}

// Look up a CapabilitySpec by an action string that may or may not exist
export function byActionType(type: string): CapabilitySpec | undefined {
  return ActionTypeMap[type]?.spec;
}

export function byEvent(event: Event): CapabilitySpec {
  const record = EventTypeMap[event];

  if (!record) {
    // Only possible if some other code forgot to validate dynamic values
    throw new Error(`Unknown event: ${event}`);
  }

  return record.spec;
}

export function byAction(action: Action): CapabilitySpec {
  const record = ActionTypeMap[action];

  if (!record) {
    // Only possible if some earlier code forgot to validate dynamic values
    throw new Error(`Unknown action: ${action}`);
  }

  return record.spec;
}

export function isValidConfig(config: unknown): config is CapabilityConfig {
  if (typeof config !== 'object' || config === null) {
    return false;
  }

  if (!('type' in config) || typeof config.type !== 'string' || !config.type) {
    return false;
  }

  const spec = byId(config.type);

  return spec?.isValidConfig(config as CapabilityConfig) === true;
}

export function isValidManifest(
  proto?: { capabilities: CapabilityConfig[] } | null,
): {
  isValid: boolean;
  error?: string;
} {
  if (typeof proto !== 'object' || proto === null) {
    return { isValid: false, error: 'Manifest is not an object' };
  }

  if (!('capabilities' in proto) || !Array.isArray(proto.capabilities)) {
    return {
      isValid: false,
      error: 'Manifest capabilities property is not array',
    };
  }

  const isValidArray: [string, boolean][] = proto.capabilities.map((c) => [
    c.type,
    isValidConfig(c),
  ]);

  const notValid = isValidArray.filter((item) => !item[1]);

  return notValid.length === 0
    ? { isValid: true }
    : {
        isValid: false,
        error: `Manifest has invalid capabilities: ${[
          ...new Set(notValid.map((item) => item[0])),
        ].join(',')}`,
      };
}
